import React from 'react';
import styled, { css } from 'styled-components';
import get from 'lodash/get';
import { ButtonTertiary, IconArrowLeft, IconArrowRight, screenMdMin, screenSmMax, screenXsMin, spacer8, spacer16, spacer24, spacer80, Type, } from '@spotify-internal/encore-web';
import { DELAYS, fadeInAnimation, ImageAspectBox, withAnimationContext, ContentfulImage, } from '../../../../ui';
import { icons } from '../../../../Layout/Icons';
export const ANIMATION_DURATION = '600ms';
const SlideHeadline = withAnimationContext(styled.div `
  ${fadeInAnimation(DELAYS.SECOND)};
  padding-bottom: ${spacer16};
  display: inline-flex;

  h3 {
    align-self: center;
  }
  svg {
    margin-inline-end: ${spacer8};
  }
`);
const StyledSlideImage = styled(ImageAspectBox) `
  max-height: 200px;
  margin-bottom: ${spacer24};
  img {
    width: unset;
    left: unset;
  }
`;
const SlideBody = withAnimationContext(styled(Type.p).attrs({
    variant: 'ballad',
}) `
  ${fadeInAnimation(DELAYS.THIRD)};
  padding-bottom: ${spacer24};
`);
const StyledSlides = styled.div `
  overflow: hidden;
  display: grid;
  grid-template-areas: 'inner-div';
`;
const slidePadding = css `
  padding-inline: ${spacer24};
  @media (min-width: ${screenXsMin}) and (max-width: ${screenSmMax}) {
    padding-inline: ${spacer80};
  }
`;
const StyledSlideContainer = styled.div `
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: ${screenSmMax}) {
    text-align: center;
    img {
      width: 100%;
    }
  }
`;
const StyledCarouselSlide = styled.div `
  grid-area: inner-div;
  ${slidePadding}

  animation-duration: ${ANIMATION_DURATION};
  visibility: hidden;
  opacity: 0;

  --slide-out-to: ${({ direction }) => direction === 'previous' ? '100%' : '-100%'};
  --slide-in-from: ${({ direction }) => direction === 'previous' ? '-100%' : '100%'};
  --slide-in-to: ${({ direction }) => direction === 'previous' ? '3%' : '-3%'};

  &.active {
    ${({ direction }) => (direction ? 'animation-name: enter' : '')};
    visibility: visible;
    opacity: 1;
  }

  &.previous {
    ${({ direction }) => (direction ? 'animation-name: exit' : '')};
  }

  @keyframes exit {
    0% {
      transform: translateX(0%);
      visibility: visible;
      opacity: 1;
      animation-timing-function: ease-out;
    }
    25% {
      transform: translateX(var(--slide-out-to));
      visibility: visible;
      opacity: 1;
    }
    100% {
      transform: translateX(var(--slide-out-to));
      visibility: hidden;
      opacity: 1;
    }
  }

  @keyframes enter {
    0% {
      transform: translateX(var(--slide-in-from));
      visibility: hidden;
    }
    20% {
      transform: translateX(var(--slide-in-from));
      visibility: visible;
      animation-timing-function: cubic-bezier(0.14, 0.63, 0.45, 1);
    }
    60% {
      transform: translateX(var(--slide-in-to));
      visibility: visible;
      animation-timing-function: ease-out;
    }
    100% {
      transform: translateX(0%);
      visibility: visible;
    }
  }
`;
const SlideImage = (props) => {
    return React.createElement(ContentfulImage, { asset: props.asset });
};
const SlideNav = styled.div `
  display: flex;
  flex-direction: row;
  align-items: center;
  @media (max-width: ${screenSmMax}) {
    justify-content: center;
  }
  ${slidePadding}
`;
const StyledPrevious = styled(ButtonTertiary) `
  transition: background-color 0.2s ease-in-out;
  :hover:not(:disabled) {
    background-color: rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${screenSmMax}) {
    order: 1;
  }
`;
const StyledNext = styled(ButtonTertiary) `
  transition: background-color 0.2s ease-in-out;
  :hover:not(:disabled) {
    background-color: rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${screenSmMax}) {
    order: 3;
  }
`;
const SlideStatus = styled(Type).attrs({
    variant: 'viola',
}) `
  @media (min-width: ${screenMdMin}) {
    padding-inline-start: ${spacer16};
  }

  @media (max-width: ${screenSmMax}) {
    order: 2;
    padding-inline-start: ${spacer8};
    padding-inline-end: ${spacer8};
  }
`;
export const SlideContainer = (props) => {
    const formatter = Intl.NumberFormat(props.locale);
    return (React.createElement(StyledSlideContainer, null,
        React.createElement(StyledSlides, null, props.children),
        React.createElement(SlideNav, null,
            React.createElement(StyledPrevious, { onClick: props.handlePrev, disabled: props.index <= 1, iconOnly: () => (React.createElement(IconArrowLeft, { semanticColor: "textBase", iconSize: 24 })) }),
            React.createElement(StyledNext, { onClick: props.handleNext, disabled: props.index >= props.slideCount, iconOnly: () => (React.createElement(IconArrowRight, { semanticColor: "textBase", iconSize: 24 })) }),
            React.createElement(SlideStatus, null,
                formatter.format(props.index),
                " / ",
                formatter.format(props.slideCount)))));
};
export const Slide = ({ slideImage, headline = '', svgIcon = '', bodyText = '', className = '', direction = '', }) => {
    const CarouselIcon = get(icons, svgIcon);
    return (React.createElement(StyledCarouselSlide, { className: className, direction: direction },
        slideImage && (React.createElement(StyledSlideImage, { fit: "contain", ratio: slideImage.file.details.image.width ||
                1 / slideImage.file.details.image.height ||
                1 },
            React.createElement(SlideImage, { asset: slideImage }))),
        !!headline && (React.createElement(SlideHeadline, null,
            svgIcon && React.createElement(CarouselIcon, { iconSize: 32 }),
            React.createElement(Type, { as: "h3", variant: "canon" }, headline))),
        React.createElement(SlideBody, null, bodyText)));
};
//# sourceMappingURL=Slide.js.map